// meetingRooms/hooks/usePricing.js - Updated to avoid reloads and use smooth feedback
import { useState, useRef } from 'react';
import { getTariffs, createTariff, updateTariff } from 'api';

export const usePricing = (allMeetingRooms, showSnackbar) => {
    const [pricingFormData, setPricingFormData] = useState({});
    const [originalPricingData, setOriginalPricingData] = useState({});
    const [pricingHasChanges, setPricingHasChanges] = useState({});
    const [isSavingPricing, setIsSavingPricing] = useState(false);
    const [isLoadingTariffs, setIsLoadingTariffs] = useState({});
    const [showNewTariffForm, setShowNewTariffForm] = useState({});
    const isSavingRef = useRef(false);

    // Fetch tariffs for a specific meeting room
    const fetchTariffs = async (meetingRoomSlug) => {
        const meetingRoom = allMeetingRooms.find(mr => mr.slug === meetingRoomSlug);
        if (!meetingRoom) return [];

        setIsLoadingTariffs(prev => ({ ...prev, [meetingRoomSlug]: true }));

        try {
            const response = await getTariffs({
                resource: meetingRoom.slug,
                booking_type: 'MR'
            });

            if (response?.tariffs && response.tariffs.length > 0) {
                // Filter tariffs for this specific resource
                const filteredTariffs = response.tariffs.filter(tariff => {
                    if (typeof tariff.resource === 'number') {
                        return meetingRoom.id ? tariff.resource === meetingRoom.id : false;
                    } else if (typeof tariff.resource === 'string') {
                        return tariff.resource === meetingRoom.slug;
                    } else if (tariff.resource && tariff.resource.slug) {
                        return tariff.resource.slug === meetingRoom.slug;
                    }
                    return false;
                });
                
                return filteredTariffs;
            }
            return [];
        } catch (error) {
            console.error('Error fetching tariffs:', error);
            showSnackbar('Failed to load tariff data', 'error');
            return [];
        } finally {
            setIsLoadingTariffs(prev => ({ ...prev, [meetingRoomSlug]: false }));
        }
    };

    // Handle creating a new tariff - Updated to avoid reload
    const handleCreateTariff = async (meetingRoomSlug, tariffData) => {
        const meetingRoom = allMeetingRooms.find(mr => mr.slug === meetingRoomSlug);
        if (!meetingRoom) {
            throw new Error("Meeting room not found");
        }

        try {
            setIsSavingPricing(true);
            isSavingRef.current = true;

            const tariffToCreate = {
                ...tariffData,
                resource: meetingRoom.id,
                booking_type: 'MR'
            };

            const result = await createTariff(tariffToCreate);

            if (result) {
                // ✅ UPDATE LOCAL STATE OPTIMISTICALLY - No fetchTariffs() call
                setPricingFormData(prev => ({
                    ...prev,
                    [meetingRoomSlug]: [...(prev[meetingRoomSlug] || []), result]
                }));

                // Hide the new tariff form
                setShowNewTariffForm(prev => ({
                    ...prev,
                    [meetingRoomSlug]: false
                }));

                // ✅ SMOOTH SNACKBAR FEEDBACK
                showSnackbar('New tariff created successfully', 'success');
                return result;
            }
        } catch (error) {
            console.error('Error creating tariff:', error);
            showSnackbar('Failed to create tariff: ' + error.message, 'error');
            throw error;
        } finally {
            setIsSavingPricing(false);
            isSavingRef.current = false;
        }
    };

    // Handle updating a tariff - Updated to avoid reload
    const handleUpdateTariff = async (meetingRoomSlug, tariffSlug, tariffData) => {
        try {
            setIsSavingPricing(true);
            isSavingRef.current = true;

            const updateData = {
                slug: tariffSlug,
                tariff: tariffData
            };

            const result = await updateTariff(updateData);

            if (result) {
                // ✅ UPDATE LOCAL STATE OPTIMISTICALLY - No fetchTariffs() call
                setPricingFormData(prev => ({
                    ...prev,
                    [meetingRoomSlug]: (prev[meetingRoomSlug] || []).map(tariff => 
                        tariff.slug === tariffSlug 
                            ? { ...tariff, ...result } 
                            : tariff
                    )
                }));

                // ✅ SMOOTH SNACKBAR FEEDBACK
                showSnackbar('Tariff updated successfully', 'success');
                return result;
            }
        } catch (error) {
            console.error('Error updating tariff:', error);
            showSnackbar('Failed to update tariff: ' + error.message, 'error');
            throw error;
        } finally {
            setIsSavingPricing(false);
            isSavingRef.current = false;
        }
    };

    // Handle deleting a tariff - Updated to avoid reload
    const handleDeleteTariff = async (meetingRoomSlug, tariffSlug) => {
        try {
            setIsSavingPricing(true);
            isSavingRef.current = true;
            
            // TODO: Add actual delete API call here when needed

            // ✅ UPDATE LOCAL STATE OPTIMISTICALLY - No fetchTariffs() call
            setPricingFormData(prev => ({
                ...prev,
                [meetingRoomSlug]: (prev[meetingRoomSlug] || []).filter(tariff => 
                    tariff.slug !== tariffSlug
                )
            }));

            // ✅ SMOOTH SNACKBAR FEEDBACK
            showSnackbar('Tariff deleted successfully', 'success');

        } catch (error) {
            console.error('Error deleting tariff:', error);
            showSnackbar('Failed to delete tariff: ' + error.message, 'error');
            throw error;
        } finally {
            setIsSavingPricing(false);
            isSavingRef.current = false;
        }
    };

    // Toggle new tariff form visibility
    const handleToggleNewTariffForm = (meetingRoomSlug) => {
        setShowNewTariffForm(prev => ({
            ...prev,
            [meetingRoomSlug]: !prev[meetingRoomSlug]
        }));
    };

    // Initialize pricing data for meeting rooms
    const initializePricing = async (meetingRooms) => {
        const newPricingFormData = {};
        const newOriginalPricingData = {};

        for (const meetingRoom of meetingRooms) {
            // Fetch tariffs for each meeting room
            const tariffs = await fetchTariffs(meetingRoom.slug);
            
            newPricingFormData[meetingRoom.slug] = tariffs;
            newOriginalPricingData[meetingRoom.slug] = [...tariffs];
        }

        setPricingFormData(newPricingFormData);
        setOriginalPricingData(newOriginalPricingData);
    };

    // No need for general "save" function since tariffs are saved individually
    const handleSavePricing = async (meetingRoomSlug) => {
        // This is a no-op since pricing saves happen immediately
        // But we keep it for consistency with other hooks
        return Promise.resolve();
    };

    const handleCancelPricing = (meetingRoomSlug) => {
        // Reset to original data if needed
        const originalData = originalPricingData[meetingRoomSlug];
        if (originalData) {
            setPricingFormData(prev => ({
                ...prev,
                [meetingRoomSlug]: [...originalData]
            }));
        }

        // Hide new tariff form
        setShowNewTariffForm(prev => ({
            ...prev,
            [meetingRoomSlug]: false
        }));

        showSnackbar('Pricing changes cancelled', 'info');
    };

    return {
        pricingFormData,
        pricingHasChanges,
        isSavingPricing,
        isLoadingTariffs,
        showNewTariffForm,
        isSavingRef,
        fetchTariffs,
        handleCreateTariff,
        handleUpdateTariff,
        handleDeleteTariff,
        handleToggleNewTariffForm,
        handleSavePricing,
        handleCancelPricing,
        initializePricing
    };
};